import { CrmObject, CrmType, UserCrmSettings } from "@/types/crm-settings";
import { http } from "../httpHandler";
import { links } from "@/constants";
import {
  ConversationTask,
  CrmActivitiesResponse,
  TServerErrorResponse,
} from "@/types";
import type { TaskRecipientType } from "@/components/common/SearchableSelect/SearchableSelectCrm";

export const GET_USER_CRM_SETTINGS = "get-user-crm-settings";
export const USER_CRM_GET_OPTIONS = "user-get-crm-settings";
export const ACCOUNTS_CRM_GET = "account-crm-get";
export const CLIENTS_CRM_GET = "clients-crm-get";
export const USERS_CRM_GET = "users-crm-get";
export const CURRENT_CRM_USER_GET = "current-crm-user-get";
export const CONTACTS_CRM_GET_OPTIONS = "contacts-get-crm-settings";
export const ACCOUNTS_CRM_GET_OPTIONS = "accounts-get-crm-settings";
export const USER_CRM_UPDATE_SETTINGS = "user-update-salesforce-settings";
export const CRM_ACTIVITIES_GET = "crm-activities-get";
const VALID_RECIPIENT_TYPES = new Set<TaskRecipientType>([
  "CONTACT",
  "ACCOUNT",
  "LEAD",
]);

const validateRecipientType = (
  type?: string,
): TaskRecipientType | undefined => {
  if (!type) return undefined;
  if (VALID_RECIPIENT_TYPES.has(type as TaskRecipientType)) {
    return type as TaskRecipientType;
  }
  return undefined;
};

export type ContactSearchContext = "task" | "summary";

export interface GetPaginatedParams {
  take: number;
  skip: number;
  search: string;
  asc: boolean;
  context?: ContactSearchContext;
}

export const getCRMSettingsByType = async () => {
  try {
    const response = await http.get<UserCrmSettings>(links.crm.settings);
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const saveUserCrmSettings = async (
  data: Partial<UserCrmSettings>,
  type: CrmType,
) => {
  try {
    const response = await http.post<UserCrmSettings>(
      links.crm.settingsByType(type),
      { ...data },
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const connectRedtail = async (data: {
  login: string;
  password: string;
}) => {
  try {
    const response = await http.post<UserCrmSettings>(
      links.userCrmSettings.connectRedtail,
      { ...data },
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const getCrmContacts = async (params?: Partial<GetPaginatedParams>) => {
  try {
    const response = await http.get<CrmObject[]>(links.crm.contacts, {
      params: { ...params },
    });

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const getCrmAccounts = async (params?: Partial<GetPaginatedParams>) => {
  try {
    const response = await http.get<CrmObject[]>(links.crm.accounts, {
      params: { ...params },
    });

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const getCrmActivities = async (
  conversationId: string,
): Promise<CrmActivitiesResponse> => {
  try {
    const response = await http.get<CrmActivitiesResponse>(
      links.crm.activities,
      {
        params: { conversationId },
      },
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const getCrmUsers = async (params?: Partial<GetPaginatedParams>) => {
  try {
    const response = await http.get<CrmObject[]>(links.crm.users, {
      params: { ...params },
    });
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const getCurrentCrmUser = async (): Promise<{
  id: string;
  name: string;
} | null> => {
  try {
    const response = await http.get<{ id: string; name: string } | null>(
      links.crm.usersCurrent,
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const CRM_USER_BY_ID_GET = "crm-user-by-id-get";

export const getCrmUserById = async (
  crmUserId: string,
): Promise<{ id: string; name: string } | null> => {
  try {
    const response = await http.get<{ id: string; name: string } | null>(
      links.crm.userById(crmUserId),
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const saveCrmObjects = async (
  conversationId: string,
  id: string,
  recipientType?: TaskRecipientType,
  crmActivityId?: string,
  createNewActivity?: boolean,
): Promise<ConversationTask> => {
  const validatedRecipientType = validateRecipientType(recipientType);
  // `createNewActivity` is not the same as an absent `crmActivityId`: the
  // backend reuses the activity the last push created when the advisor
  // expressed no preference, and only this flag says they did.
  const body =
    validatedRecipientType || crmActivityId || createNewActivity
      ? {
          ...(validatedRecipientType && {
            recipientType: validatedRecipientType,
          }),
          ...(crmActivityId && { crmActivityId }),
          ...(createNewActivity && { createNewActivity: true }),
        }
      : undefined;
  try {
    const response = await http.post(
      links.crm.pushSummary(conversationId, id),
      body,
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};
